<template>
  <BContainer fluid>
    <div id="two-column-menu"></div>

    <ul class="navbar-nav h-100" id="navbar-nav">

      <!-- Menu com link direto (sem submenus) -->
      <li class="nav-item" v-for="menu in menus" :key="menu.id">
        <router-link
          v-if="menu.path"
          class="nav-link menu-link"
          :to="'/app' + menu.path"
        >
          <i :class="menu.icon"></i>
          <span>{{ menu.name }}</span>
        </router-link>

        <!-- Menu com submenus (collapse) -->
        <div v-else>
          <a
            class="nav-link menu-link"
            :href="`#menu-${menu.id}`"
            data-bs-toggle="collapse"
            role="button"
            aria-expanded="false"
            :aria-controls="`menu-${menu.id}`"
          >
            <i :class="menu.icon"></i>
            <span>{{ menu.name }}</span>
          </a>
          <div class="collapse menu-dropdown" :id="`menu-${menu.id}`">
            <ul class="nav nav-sm flex-column">
              <li class="nav-item" v-for="sub in menu.Submenu" :key="sub.id">
                <router-link
                  :to="'/app' + sub.path"
                  class="nav-link"
                  :data-key="sub.name"
                >
                  {{ sub.name }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </li>
    </ul>
  </BContainer>
</template>

<script>
import { layoutComputed } from "@/state/helpers";
import { ehAdmin } from '@/helpers/permissao';
import { api } from "@/helpers/common/api";

/**
 * Componente de menu lateral/vertical.
 *
 * Carrega menus do backend (GET /menu?where={"status":1}&include=["Submenu"])
 * e filtra por permissao:
 *   - admin do espaco (ou da plataforma) -> ve todos os menus
 *   - caso contrario -> apenas os menus liberados no VINCULO com este espaco
 *
 * O papel e a lista de menus chegam no login, vindos de `usuario_tenant`:
 * a mesma pessoa pode ser admin num diretorio e atendente em outro.
 */
export default {
  data() {
    return {
      menus: [],
      user: {},
    };
  },

  computed: {
    ...layoutComputed,
    layoutType() {
      return this.$store ? this.$store.state.layout.layoutType : "vertical";
    },
  },

  watch: {
    $route: {
      handler: "onRoutechange",
      immediate: true,
      deep: true,
    },
  },

  async created() {
    this.getUser();
    await this.carregarMenus();
  },

  mounted() {
    this.initActiveMenu();

    try {
      document.getElementById("overlay").addEventListener("click", () => {
        document.body.classList.remove("vertical-sidebar-enable");
      });
    } catch (_e) {
      // overlay pode nao existir
    }

    if (document.querySelectorAll(".navbar-nav .collapse")) {
      const collapses = document.querySelectorAll(".navbar-nav .collapse");
      collapses.forEach((collapse) => {
        collapse.addEventListener("show.bs.collapse", (e) => {
          e.stopPropagation();
          const closestCollapse = collapse.parentElement.closest(".collapse");
          if (closestCollapse) {
            const siblingCollapses = closestCollapse.querySelectorAll(".collapse");
            siblingCollapses.forEach((sc) => {
              if (sc.classList.contains("show")) {
                sc.classList.remove("show");
                sc.parentElement.firstChild.setAttribute("aria-expanded", "false");
              }
            });
          } else {
            const getSiblings = (elem) => {
              const siblings = [];
              let sibling = elem.parentNode.firstChild;
              while (sibling) {
                if (sibling.nodeType === 1 && sibling !== elem) siblings.push(sibling);
                sibling = sibling.nextSibling;
              }
              return siblings;
            };
            const siblings = getSiblings(collapse.parentElement);
            siblings.forEach((item) => {
              if (item.childNodes.length > 2) {
                item.firstElementChild.setAttribute("aria-expanded", "false");
                item.firstElementChild.classList.remove("active");
              }
              const ids = item.querySelectorAll("*[id]");
              ids.forEach((item1) => {
                item1.classList.remove("show");
                item1.parentElement.firstChild.setAttribute("aria-expanded", "false");
                item1.parentElement.firstChild.classList.remove("active");
                if (item1.childNodes.length > 2) {
                  const links = item1.querySelectorAll("ul li a");
                  links.forEach((subitem) => {
                    if (subitem.hasAttribute("aria-expanded"))
                      subitem.setAttribute("aria-expanded", "false");
                  });
                }
              });
            });
          }
        });

        collapse.addEventListener("hide.bs.collapse", (e) => {
          e.stopPropagation();
          const childCollapses = collapse.querySelectorAll(".collapse");
          childCollapses.forEach((cc) => {
            cc.classList.remove("show");
            cc.parentElement.firstChild.setAttribute("aria-expanded", "false");
          });
        });
      });
    }
  },

  methods: {
    normalizarMenusComCamposPersonalizados(menus) {
      if (!Array.isArray(menus)) return [];

      const menuCadastros = menus.find((item) => item && item.name === 'Cadastros');
      if (!menuCadastros) return menus;

      if (!Array.isArray(menuCadastros.Submenu)) {
        menuCadastros.Submenu = [];
      }

      const jaExiste = menuCadastros.Submenu.some(
        (sub) => sub && (sub.path === '/cadastros/campos-personalizados' || sub.name === 'Campos Personalizados')
      );

      if (!jaExiste) {
        menuCadastros.Submenu.push({
          id: 'cadastros-campos-personalizados',
          name: 'Campos Personalizados',
          icon: 'ri-layout-grid-line',
          path: '/cadastros/campos-personalizados',
        });
      }

      menuCadastros.Submenu = menuCadastros.Submenu
        .filter((sub) => !!sub && !!sub.path)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

      return menus;
    },

    getUser() {
      this.user = JSON.parse(localStorage.getItem("userdata")) || {};
    },

    async carregarMenus() {
      try {
        let where = { status: 1 };

        // Filtro de permissao: admin ve tudo; os demais, so os menus liberados
        // no VINCULO com este espaco (usuario_tenant.menus).
        const admin = ehAdmin(this.user);
        if (!admin && Array.isArray(this.user.menus) && this.user.menus.length) {
          where.id = this.user.menus;
        } else if (!admin) {
          // Sem permissoes definidas e nao e admin: nao mostra nada
          this.menus = [];
          return;
        }

        const whereStr = JSON.stringify(where);
        const result = await api.read(`/menu?where=${whereStr}&include=["Submenu"]`);
        this.menus = this.normalizarMenusComCamposPersonalizados(Array.isArray(result) ? result : []);
      } catch (_e) {
        this.menus = [];
      }
    },

    onRoutechange(ele) {
      this.initActiveMenu(ele.path);
    },

    initActiveMenu() {
      const pathName = window.location.pathname;
      const ul = document.getElementById("navbar-nav");
      if (ul) {
        const items = Array.from(ul.querySelectorAll("a.nav-link"));
        const activeItems = items.filter((x) => x.classList.contains("active"));
        this.removeActivation(activeItems);
        const matchingMenuItem = items.find((x) => x.getAttribute("href") === pathName);
        if (matchingMenuItem) {
          this.activateParentDropdown(matchingMenuItem);
        }
      }
    },

    removeActivation(items) {
      items.forEach((item) => {
        if (item.classList.contains("menu-link")) {
          if (!item.classList.contains("active")) {
            item.setAttribute("aria-expanded", false);
          }
          try {
            item.nextElementSibling.classList.remove("show");
          } catch (_e) {
            /* vazio */
          }
        }
        if (item.classList.contains("nav-link")) {
          if (item.nextElementSibling) {
            item.nextElementSibling.classList.remove("show");
          }
          item.setAttribute("aria-expanded", false);
        }
        item.classList.remove("active");
      });
    },

    activateParentDropdown(item) {
      item.classList.add("active");
      const parentCollapseDiv = item.closest(".collapse.menu-dropdown");
      if (parentCollapseDiv) {
        parentCollapseDiv.classList.add("show");
        parentCollapseDiv.parentElement.children[0].classList.add("active");
        parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
        if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
          parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
          if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling) {
            parentCollapseDiv.parentElement
              .closest(".collapse")
              .previousElementSibling.classList.add("active");
          }
        }
      }
    },
  },
};
</script>
